// ==========================================================================
// screens/MfaSetup.tsx — first login: enrol an authenticator app (FF-003)
// Supabase TOTP: enroll -> show QR -> challenge -> verify the 6-digit code.
// After verify the session is aal2 and the app opens.
// 10 Oct (FF-059, Caitlin's first login on her iPhone): a phone can't scan
// its own screen, so there's a button that hands the code straight to the
// authenticator on the same phone (the otpauth link), and the entry is named
// "O'Sullivan Family" in the authenticator.
// ==========================================================================
import { useEffect, useState, type FormEvent } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/lib/auth";
import { Brand } from "@/components/Brand";
import { Button, Card, ErrorText, Field } from "@/components/ui";

export default function MfaSetup() {
  const { refresh, signOut } = useAuth();
  const [factorId, setFactorId] = useState<string | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [uri, setUri] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      // Clear any half-finished enrolment from an earlier attempt.
      const { data: existing } = await supabase.auth.mfa.listFactors();
      for (const f of existing?.totp ?? []) {
        if (f.status !== "verified") await supabase.auth.mfa.unenroll({ factorId: f.id });
      }
      const { data, error } = await supabase.auth.mfa.enroll({ factorType: "totp", friendlyName: "Authenticator app", issuer: "O'Sullivan Family" });
      if (error || !data) { setError("Couldn't start MFA setup. Sign out and try again."); return; }
      setFactorId(data.id);
      setQr(data.totp.qr_code);
      setSecret(data.totp.secret);
      setUri(data.totp.uri);
    })();
  }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!factorId) return;
    setBusy(true);
    setError(null);
    const { data: ch, error: chErr } = await supabase.auth.mfa.challenge({ factorId });
    if (chErr || !ch) { setBusy(false); setError("Couldn't check the code. Try again."); return; }
    const { error: vErr } = await supabase.auth.mfa.verify({ factorId, challengeId: ch.id, code: code.trim() });
    setBusy(false);
    if (vErr) { setError("That code didn't match. Wait for a fresh one and try again."); return; }
    await refresh();
  }

  return (
    <main className="mx-auto max-w-md px-4 py-10 space-y-6">
      <Brand />
      <Card className="space-y-4">
        <h2 className="text-lg font-semibold">Set up your authenticator</h2>
        <ol className="list-decimal pl-5 text-sm text-ink-muted space-y-1">
          <li>On a computer: scan this code with the authenticator app on your phone.</li>
          <li>On your phone: tap "Add to my authenticator" below.</li>
          <li>Type the 6-digit code the authenticator shows.</li>
        </ol>
        {qr ? <img src={qr} alt="QR code for your authenticator app" className="mx-auto h-48 w-48 rounded-md bg-white p-2" /> : <p className="text-sm text-ink-muted">Preparing…</p>}
        {uri && (
          <a href={uri} className="flex w-full items-center justify-center rounded-md bg-surface-raised px-4 py-3 font-semibold text-primary">Add to my authenticator</a>
        )}
        {secret && (
          <p className="text-xs text-ink-muted break-all">Can't scan? Enter this key by hand: <span className="num">{secret}</span></p>
        )}
        <form onSubmit={submit} className="space-y-4">
          <Field label="6-digit code" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" required value={code} onChange={(e) => setCode(e.target.value)} />
          <ErrorText>{error}</ErrorText>
          <Button type="submit" disabled={busy || !factorId}>{busy ? "Checking…" : "Finish setup"}</Button>
          <Button type="button" variant="ghost" onClick={signOut}>Sign out</Button>
        </form>
      </Card>
    </main>
  );
}
